import { isUndefined, isNil } from 'lodash'
import { computed, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import { MAX_CUSTOM_FIELDS_COUNT } from '@/utils/custom-fields/helpers'
import { appPlatformInjectionKey } from '@/utils/injection-keys'
import { isJiraIssueOrWorkItem } from '@/utils/objective-types'
import { isOkrElementClosed, OKR_STATUSES } from '@/utils/objectives'
import { APP_PLATFORMS } from '@root/app-platforms'
import { PLUGIN_OPTIONS_KEYS } from '@root/template-options-keys'

export const SUBSCRIPTION_PLANS = {
  ESSENTIAL: '1',
  PROFESSIONAL: '2',
  ENTERPRISE: '3'
}

export const MAX_OKR_LEVELS_COUNT = Infinity
export const MAX_PUBLIC_CUSTOM_DASHBOARDS_COUNT = Infinity
export const MAX_WORKSPACES_COUNT = Infinity

export const RESTRICTIONS_KEYS = {
  OKR_LEVELS: 'okrLevels',
  CUSTOM_FIELDS: 'customField',
  OKR_WEIGHTS: 'weight',
  CUSTOM_DASHBOARD_PUBLIC: 'customDashboardPublic',
  SSO: 'sso',
  DATA_SOURCES: 'dataSources',
  WORKSPACES: 'workspace'
}

export const RESTRICTIONS_IDS = {
  ID_1: 1,
  ID_2: 2,
  ID_3: 3
}

const { ID_1, ID_2, ID_3 } = RESTRICTIONS_IDS

export const LIMITATIONS = {
  OKR_LEVELS: {
    [ID_1]: 6,
    [ID_2]: 10,
    [ID_3]: MAX_OKR_LEVELS_COUNT
  },
  CUSTOM_FIELDS: {
    [ID_1]: 3,
    [ID_2]: 10,
    [ID_3]: MAX_CUSTOM_FIELDS_COUNT
  },
  CUSTOM_DASHBOARD_PUBLIC: {
    [ID_1]: 3,
    [ID_2]: 20,
    [ID_3]: MAX_PUBLIC_CUSTOM_DASHBOARDS_COUNT
  },
  SSO: {
    [ID_1]: false,
    [ID_2]: true
  },
  DATA_SOURCES: {
    [ID_1]: false,
    [ID_2]: true
  },
  WORKSPACES: {
    [ID_1]: 1,
    [ID_2]: 5,
    [ID_3]: MAX_WORKSPACES_COUNT
  }
}

const INTERNAL_PLANS_PLATFORMS = [
  APP_PLATFORMS.WEB_APP,
  APP_PLATFORMS.CROSS_PLATFORM,
  APP_PLATFORMS.AZURE_DEVOPS
]

const JIRA_PLANS_PLATFORMS = [APP_PLATFORMS.JIRA]

/**
 * Plan tiers that can be suggested as an upgrade, grouped by the platform they exist on.
 *
 * `worth` is intentionally NOT the ordinal of the backend PlanPriority enum:
 *
 * ```kotlin
 * enum class PlanPriority(val priority: Int) {
 *     ESSENTIAL(0), STANDARD(1), PROFESSIONAL(2), ADVANCED(3), ENTERPRISE(4)
 * }
 * ```
 *
 * That enum carries a single combined value per instance. When an instance holds several plans
 * at once (e.g. a Jira Cloud connection paired with a Web app organization) the highest tier
 * wins, so a Jira app can receive a Web app tier and a Web app can receive a Jira tier.
 *
 * `worth` encodes the level of capabilities a plan gives, so tiers that are equivalent across
 * platforms share one value: Jira Standard is equal to Web app Professional, hence
 * INTERNAL_PROFESSIONAL.worth is 1 — the ordinal of STANDARD — and not its own ordinal 2.
 *
 * That is exactly what makes the strict `worth > planPriority` search correct. An Essential
 * organization with a connected Standard Jira has a combined priority of 1 and already has
 * Professional level features, so Professional must be skipped and Enterprise suggested instead.
 * Raising the value to 2 would make us offer a tier the user effectively already owns.
 */
const PLANS_PRIORITIES = {
  INTERNAL_ESSENTIAL: {
    worth: 0,
    availableFor: INTERNAL_PLANS_PLATFORMS
  },
  INTERNAL_PROFESSIONAL: {
    worth: 1,
    availableFor: INTERNAL_PLANS_PLATFORMS,
    tooltip: 'limitations.below_professional'
  },
  INTERNAL_ENTERPRISE: {
    worth: 4,
    availableFor: INTERNAL_PLANS_PLATFORMS,
    tooltip: 'limitations.below_enterprise'
  },
  JIRA_STANDARD: {
    worth: 1,
    availableFor: JIRA_PLANS_PLATFORMS
  },
  JIRA_ADVANCED: {
    worth: 3,
    availableFor: JIRA_PLANS_PLATFORMS,
    tooltip: 'limitations.below_advanced'
  }
}

const createBetterPlanFinder = appPlatform => {
  const currentPlatformPlans = Object.values(PLANS_PRIORITIES)
    .filter(t => t.availableFor.includes(appPlatform))
    .toSorted((a, b) => a.worth - b.worth)
  return currentPriority => currentPlatformPlans.find(t => t.worth > currentPriority) ?? null
}

export const usePlansLimitations = () => {
  const appPlatform = inject(appPlatformInjectionKey)

  const betterPlanFinder = createBetterPlanFinder(appPlatform)

  const store = useStore()
  const { t } = useI18n()

  const isOwner = computed(() => store.state.pluginOptions[PLUGIN_OPTIONS_KEYS.IS_OWNER])

  const restrictions = computed(() => store.state.system.restrictions || {})

  const planPriority = computed(() => store.state.system.planPriority)

  const isLimitExceeded = ({ count, limit }) => count >= limit

  const getRestrictionId = ({ key }) => restrictions.value[key]

  const checkIsNeedLimitation = ({ restrictionId }) => !isUndefined(restrictionId)

  /**
   *
   * @returns {string|null}
   */
  const getBetterPlanTooltip = computed(() => {
    if (isNil(planPriority.value)) return null

    const betterPlanTooltip = betterPlanFinder(planPriority.value)?.tooltip ?? null

    if (betterPlanTooltip) {
      return t(betterPlanTooltip)
    }

    return betterPlanTooltip
  })

  const customFieldsLimitations = computed(() => {
    const restrictionId = getRestrictionId({ key: RESTRICTIONS_KEYS.CUSTOM_FIELDS })
    const isNeedLimitation = checkIsNeedLimitation({ restrictionId })

    const limit = isNeedLimitation
      ? LIMITATIONS.CUSTOM_FIELDS[restrictionId]
      : MAX_CUSTOM_FIELDS_COUNT

    const getDisabledButtonTooltip = () => {
      const tooltipFromBetterPlan = getBetterPlanTooltip.value

      if (isNeedLimitation && tooltipFromBetterPlan) {
        return tooltipFromBetterPlan
      } else {
        return t('custom_fields.disable_create_button_tooltip', { limit })
      }
    }

    return {
      limit,
      getDisabledButtonTooltip,
      isLimitExceeded: ({ count }) => isLimitExceeded({ count, limit })
    }
  })

  const okrLevelsLimitations = computed(() => {
    const restrictionId = getRestrictionId({ key: RESTRICTIONS_KEYS.OKR_LEVELS })
    const isNeedLimitation = checkIsNeedLimitation({ restrictionId })

    const limit = isNeedLimitation ? LIMITATIONS.OKR_LEVELS[restrictionId] : MAX_OKR_LEVELS_COUNT

    const getDisabledButtonTooltip = () => {
      const tooltipFromBetterPlan = getBetterPlanTooltip.value

      if (isNeedLimitation && Number.isFinite(limit) && tooltipFromBetterPlan) {
        return tooltipFromBetterPlan
      } else {
        return null
      }
    }

    return {
      limit,
      getDisabledButtonTooltip,
      isLimitExceeded: ({ count }) => isLimitExceeded({ count, limit })
    }
  })

  const okrWeightsLimitations = computed(() => {
    const UNAVAILABLE_BY_PLAN_TOOLTIP = getBetterPlanTooltip.value

    const restrictionId = getRestrictionId({ key: RESTRICTIONS_KEYS.OKR_WEIGHTS })

    const UNAVAILABLE_RESTRICTION_ID = RESTRICTIONS_IDS.ID_1
    const isUnavailable = restrictionId === UNAVAILABLE_RESTRICTION_ID

    const isDisabled = ({ okrElement }) => {
      if (isJiraIssueOrWorkItem(okrElement)) {
        return true
      }

      const isElementInClosedStatus = isOkrElementClosed(okrElement)

      const isAvailableByChildren = Boolean(okrElement.childCount)

      if (isUnavailable) return true

      // okr weights available only if element is not closed and has more than 0 children
      return isElementInClosedStatus || !isAvailableByChildren
    }

    const getTooltip = ({ okrElement }) => {
      if (isJiraIssueOrWorkItem(okrElement)) {
        return null
      }

      const isElementInClosedStatus = isOkrElementClosed(okrElement)

      const isAvailableByChildren = Boolean(okrElement.childCount)

      const getClosedStatusName = () => {
        if (isElementInClosedStatus) {
          const statusName =
            okrElement.confidenceLevelId === OKR_STATUSES.CLOSED
              ? t('dashboard.closed')
              : t('dashboard.abandoned')
          return statusName.toLowerCase()
        }

        return ''
      }

      const tooltipByChildCount = t('weights.objective.cant_change_weight_by_nested_items')
      const tooltipByClosedStatus = t('weights.objective.cant_change_weight_desc', {
        status: getClosedStatusName()
      })

      const conditions = [
        !isAvailableByChildren && tooltipByChildCount,
        isElementInClosedStatus && tooltipByClosedStatus
      ]

      const [tooltip] = [isUnavailable && UNAVAILABLE_BY_PLAN_TOOLTIP, ...conditions].filter(
        Boolean
      )

      return tooltip || null
    }

    const cheapPlanLimitations = {
      UNAVAILABLE_BY_PLAN_TOOLTIP,
      isDisabledByPlan: isUnavailable
    }

    return {
      isDisabled,
      getTooltip,
      cheapPlanLimitations
    }
  })

  const customDashboardPublicLimitations = computed(() => {
    const restrictionId = getRestrictionId({ key: RESTRICTIONS_KEYS.CUSTOM_DASHBOARD_PUBLIC })
    const isNeedLimitation = checkIsNeedLimitation({ restrictionId })

    const limit = isNeedLimitation
      ? LIMITATIONS.CUSTOM_DASHBOARD_PUBLIC[restrictionId]
      : MAX_PUBLIC_CUSTOM_DASHBOARDS_COUNT

    const getDisabledButtonTooltip = () => {
      const tooltipFromBetterPlan = getBetterPlanTooltip.value

      if (isNeedLimitation && Number.isFinite(limit) && tooltipFromBetterPlan) {
        return tooltipFromBetterPlan
      } else {
        return null
      }
    }

    return {
      limit,
      getDisabledButtonTooltip,
      isLimitExceeded: ({ count }) => isLimitExceeded({ count, limit })
    }
  })

  const SSOLimitations = computed(() => {
    const restrictionId = getRestrictionId({ key: RESTRICTIONS_KEYS.SSO })

    const isAvailableByPlan = LIMITATIONS.SSO[restrictionId] ?? LIMITATIONS.SSO[ID_1]

    const isAvailable = isAvailableByPlan && isOwner.value

    const getDisabledButtonDescription = () => {
      if (!isAvailableByPlan) {
        return t('integration_settings.available_with_enterprise')
      }

      if (!isOwner.value) {
        return t('integration_settings.available_for_owner')
      }

      return 'SAML, SCIM'
    }

    return {
      isAvailable,
      getDisabledButtonDescription
    }
  })

  const dataSourceLimitations = computed(() => {
    const restrictionId = getRestrictionId({ key: RESTRICTIONS_KEYS.DATA_SOURCES })
    const isNeedLimitation = checkIsNeedLimitation({ restrictionId })

    const isAvailableByPlan =
      LIMITATIONS.DATA_SOURCES[restrictionId] ?? LIMITATIONS.DATA_SOURCES[ID_1]

    const isAvailable = isNeedLimitation ? isAvailableByPlan : LIMITATIONS.DATA_SOURCES[ID_2]

    return {
      isAvailable
    }
  })

  const workspacesLimitations = computed(() => {
    const restrictionId = getRestrictionId({ key: RESTRICTIONS_KEYS.WORKSPACES })
    const isNeedLimitation = checkIsNeedLimitation({ restrictionId })

    const limit = isNeedLimitation ? LIMITATIONS.WORKSPACES[restrictionId] : MAX_WORKSPACES_COUNT

    const getDisabledButtonTooltip = () => {
      const tooltipFromBetterPlan = getBetterPlanTooltip.value

      if (isNeedLimitation && Number.isFinite(limit) && tooltipFromBetterPlan) {
        return tooltipFromBetterPlan
      }

      return null
    }

    return {
      limit,
      getDisabledButtonTooltip,
      isLimitExceeded: ({ count }) => isLimitExceeded({ count, limit })
    }
  })

  return {
    customFieldsLimitations,
    okrLevelsLimitations,
    okrWeightsLimitations,
    customDashboardPublicLimitations,
    SSOLimitations,
    dataSourceLimitations,
    workspacesLimitations
  }
}
